import {Module} from '@gongxigames/framework/slot/fragments/module/Module';
import {GlobalEventsMap} from '@gongxigames/framework/slot/events';
import {ModuleInitPhase} from '@gongxigames/framework/core';
import {
    HTMLSystemPopup,
    Message,
    MessageButton, MessageButtonAction,
    MessagePopup,
    MessagePopupButton,
    MessagePopupData,
    SystemPopup
} from '@gongxigames/framework/slot/types';
import {MessagePopupComponent} from './popup/MessagePopupComponent';
import {HTMLMessagePopup} from './popup/html/HTMLMessagePopup';
import {slotStore} from '@gongxigames/framework/slotV2/globals';

export class MessageModule extends Module<null, GlobalEventsMap> {
    public static NAME = Symbol('MessageModule');
    public static initPhase: ModuleInitPhase = 'initial';

    private _currentMessagePopups: SystemPopup[] = [];

    public init(): void {
        this.setupEvents();
    }

    protected setupEvents(): void {
        this.eventManager.on('General.Message.SHOW', this.onShow.bind(this), this);
        this.eventManager.on('General.Message.CLOSE', this.onClose.bind(this), this);
    }

    protected onShow(message: Message): void {
        const popup = message?.rendererType === 'HTML' ? this.getHTMLPopup(message) : this.getPopup(message);

        this.showPopup(popup);
    }

    protected onClose(message: Message): void {
        if (message) {
            this.closePopupById(message.id);
        } else {
            this.closePopup();
        }
    }

    protected emitOpenCashier(): void {
        this._eventManager.emit('General.Window.OPEN_CASHIER');
    }

    protected emitOpenHistory(): void {
        this._eventManager.emit('General.Window.OPEN_HISTORY');
    }

    protected emitReload(): void {
        this._eventManager.emit('General.Window.RELOAD');
    }

    protected emitHomeAction(): void {
        this.eventManager.emit('General.Window.GO_TO_HOME');
    }

    protected showPopup(popup: SystemPopup): void {
        this._currentMessagePopups.push(popup);
        this.emitAddPopup(popup);
    }

    protected closePopupById(id: string): void {
        if (this.currentMessagePopup?.id === id) {
            this.closePopup();
        }
    }

    get currentMessagePopup() {
        if (this._currentMessagePopups.length === 0) return undefined;
        return this._currentMessagePopups[this._currentMessagePopups.length - 1];
    }

    protected closePopup(): void {
        if (this.currentMessagePopup) {
            this.emitRemovePopup(this.currentMessagePopup);
            this._currentMessagePopups.pop();
        }
    }

    protected emitRemovePopup(popup: SystemPopup): void {
        this._eventManager.emit('General.Popups.Remove', popup);
    }

    protected emitAddPopup(popup: SystemPopup): void {
        this.eventManager.emit('General.Popups.Add', popup);
    }

    protected getPopupData(params: Message): MessagePopupData {
        return {
            title: params.title,
            message: params.message,
            buttons: params.buttons ? this.getButtonsConfig(params.buttons) : [],
            withBorders: !params.withoutBorder,
            closeButton: {
                visible: params.closeButton || false,
                onClick:  () => this.closePopupById(params.id)
            },
            safeZoneContainer: slotStore.slot_isGameStateMachineActive.value ? slotStore.stage_safeZoneState_ui : undefined

        };
    }

    protected getPopup(params: Message): MessagePopup {
        return {
            id: params.id,
            priority: params.priority || 0,
            type: 'ReactPIXI',
            data: this.getPopupData(params),
            view: MessagePopupComponent
        };
    }

    protected getHTMLPopup(params: Message): HTMLSystemPopup {
        return {
            id: params.id,
            priority: params.priority || 0,
            type: 'HTML',
            data: {
                title: params.title,
                message: params.message
            },
            view: HTMLMessagePopup.createPopup(this.getPopupData(params) )
        };
    }

    protected getButtonsConfig(buttons: MessageButton[]): any[] {
        return buttons.map(data => this.getButtonConfig(data)).filter(btn => btn);
    }

    protected getButtonConfig(button: MessageButton): MessagePopupButton | undefined {
        const buttonHandler = this.getButtonHandler(button.action);
        const {actionCallback = () => {}} = button;

        if (buttonHandler) {
            return {
                title: button.title,
                action: () => {
                    buttonHandler();
                    actionCallback();
                }
            };
        }

        return undefined;
    }

    protected getButtonHandler(action: MessageButtonAction): Function | undefined {
        let handler;

        switch (action) {
            case 'close_message':
                handler = this.closePopup.bind(this);
                break;
            case 'reload': {
                handler = this.emitReload.bind(this);
                break;
            }
            case 'go_to_home':
                handler = this.emitHomeAction.bind(this);
                break;
            case 'deposit':
            case 'cashier':
                if (this._settings.get('cashierUrl')) {
                    handler = () => {
                        this.emitOpenCashier();
                        this.closePopup();
                    };
                }
                break;
            case 'history':
                if (this._settings.get('historyUrl')) {
                    handler = () => {
                        this.emitOpenHistory();
                        this.closePopup();
                    };
                }
                break;
            case 'none':
                handler = () => {};
                break;
            default:
        }

        return handler;
    }
}
